import { advanceJourney, linkProgress, SIGN_SPACING } from './skyJourney';
import { makeStarSprites, pickTint, STAR_TINTS } from './starSprites';
import { CLASS_SIGNS } from '../../utils/arena/classSigns.data';
import { Component } from 'preact';
import { createNebulaRenderer, paletteFor, shiftFor } from './nebulaGl';
import { HUMOURS_SIGN, FIGURE_KEYS, FIELD_W, FIELD_H, loadFigureFields, easeFigureWeights, figureRect, sampleFigureDensity, shapeCloudDensity } from './nebulaFigure';

// Heading of the star field for a class colour: the sky swings to a
// different part of the night when the sign under the eye changes.
function headingFor(key) {
  let h = 2166136261;
  for (const ch of String(key || 'sky')) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return (((h >>> 0) % 1000) / 1000) * 0.9 - 0.45;
}

// A living night sky behind the class screens.
//
// Three depths of stars turn slowly about a pole above the sky on one
// canvas, each layer at its own rate, and lean with the pointer so the
// sky has depth. Now and then a
// meteor crosses. Behind the stars a nebula is computed live on the GPU
// (nebulaGl.js): its warp offset and palette ease toward the class
// colour under the eye, so a class change makes the clouds flow into a
// new shape while their colour drifts, instead of cutting. Where WebGL
// is unavailable the clouds are painted once on 2D canvases instead.
// An aurora band breathes on a long CSS loop. The pointer lean is
// published as `--vh-par-x` / `--vh-par-y` on the parent element, so
// the constellation can follow at its own depth through CSS.
//
// The figure reshapes the original noise inside each cloud layer before
// color or opacity is calculated. Existing stars respond to that density;
// no figure emission, line art, or separate particle pass is composited.
// All five weights ease independently through interrupted previews.
// One shared rAF drives the clouds and stars. Pauses while the
// tab is hidden. Under reduced motion it draws a single still frame,
// with no drift, no meteors and no pointer lean.

// Share of the figure rect every sign is scaled to fill in height.
const SIGN_HEIGHT = 0.86;
const SEED = 7;
// The star field is laid out on a disc around a pole above the top edge
// (canvas-width units; 0.5625 is the 16:9 height). The whole sky turns
// about that pole the way the night sky turns about Polaris, each layer
// at its own rate so depth shows as parallax. The disc reaches the far
// corners at every angle, so the frame never empties as it turns.
const POLE = { x: 0.5, y: -0.9 };
const FIELD_R = 1.55;
const LAYERS = [
  { count: 6800, rMin: 0.3, rMax: 0.8, aMin: 0.16, aMax: 0.5, rot: 0.0062, swing: 0.55, parallax: 3, glow: false },
  { count: 2200, rMin: 0.6, rMax: 1.3, aMin: 0.32, aMax: 0.8, rot: 0.0084, swing: 0.85, parallax: 7, glow: false },
  { count: 720, rMin: 1.2, rMax: 2.4, aMin: 0.55, aMax: 1, rot: 0.0112, swing: 1.2, parallax: 14, glow: true },
];

function seeded(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function makeLayers() {
  const rnd = seeded(SEED);
  return LAYERS.map((layer) => ({
    ...layer,
    stars: Array.from({ length: layer.count }, () => ({
      // uniform over the disc: radius by sqrt so density is even
      dist: Math.sqrt(rnd()) * FIELD_R,
      ang: rnd() * Math.PI * 2,
      r: layer.rMin + rnd() * (layer.rMax - layer.rMin),
      base: layer.aMin + rnd() * (layer.aMax - layer.aMin),
      speed: 0.2 + rnd() * 0.7,
      phase: rnd() * Math.PI * 2,
      wander: 0.6 + rnd() * 1.8,
      tint: pickTint(rnd()),
      // Only a few of the near stars are bright enough to throw spikes.
      spike: layer.glow && rnd() < 0.1,
      flick: 0.1 + rnd() * 0.22,
    })),
  }));
}

// Solid colour per tint for the far stars, which are single pixels.
const STAR_CSS = STAR_TINTS.map(({ rgb }) => `rgb(${rgb.map((v) => Math.round(v * 255)).join(',')})`);


// ── Nebula ─────────────────────────────────────────────────────────
// Painted once per class colour at low resolution and stretched over
// the sky by CSS, which blurs it for free. Value-noise fBm gives the
// cloud shapes; two fields are combined so the class colour, a cooler
// companion hue and dark dust all read as one cloud, not three layers.

const NEBULA_W = 320;
const NEBULA_H = 180;
const NEBULA_GL_W = 512;
const NEBULA_GL_H = 288;

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [143, 168, 200];
}

function hash2(x, y, seed) {
  let h = (x * 374761393 + y * 668265263 + seed * 1442695041) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function valueNoise(x, y, seed) {
  const xi = Math.floor(x); const yi = Math.floor(y);
  const fx = x - xi; const fy = y - yi;
  const sx = fx * fx * (3 - 2 * fx); const sy = fy * fy * (3 - 2 * fy);
  const a = hash2(xi, yi, seed); const b = hash2(xi + 1, yi, seed);
  const c = hash2(xi, yi + 1, seed); const d = hash2(xi + 1, yi + 1, seed);
  return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
}

function fbm(x, y, seed, octaves = 5) {
  let v = 0; let amp = 0.5; let f = 1; let norm = 0;
  for (let i = 0; i < octaves; i++) {
    v += valueNoise(x * f, y * f, seed + i * 17) * amp;
    norm += amp; amp *= 0.5; f *= 2.05;
  }
  return v / norm;
}

function smoothstep(a, b, x) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

function makeNebula(accent, seed, densityAt = () => 0, formation = 0) {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = NEBULA_W; c.height = NEBULA_H;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const img = ctx.createImageData(NEBULA_W, NEBULA_H);
  const [ar, ag, ab] = hexToRgb(accent);
  // Companion hue: rotate toward blue-violet so the cloud is not one flat tint.
  // Companion hue leans violet, the ridges warm gold, so the cloud has
  // the mixed light of a painted nebula instead of one flat tint.
  const [cr, cg, cb] = paletteFor(accent).mid.map((value) => value * 255);
  const wr = 240; const wg = 190; const wb = 120;
  const data = img.data;
  for (let y = 0; y < NEBULA_H; y++) {
    for (let x = 0; x < NEBULA_W; x++) {
      const nx = x / NEBULA_W * 3.2; const ny = y / NEBULA_H * 1.9;
      // Domain-warp the first field so the clouds curl instead of blobbing.
      const wx = fbm(nx + 5.2, ny + 1.3, seed + 3, 3) * 1.6;
      const wy = fbm(nx - 2.1, ny + 7.7, seed + 9, 3) * 1.6;
      const n1 = fbm(nx + wx, ny + wy, seed);
      const n2 = fbm(nx * 1.7 + 11, ny * 1.7 - 4, seed + 41, 4);
      const dust = fbm(nx * 0.9 - 3, ny * 0.9 + 9, seed + 77, 3);
      const wisps = fbm(nx * 9 + wx, ny * 9 + wy, seed + 19);
      const cloud = smoothstep(0.32, 0.74, n1 + (wisps - 0.5) * 0.16) * (0.8 + wisps * 0.5);
      const ridge = smoothstep(0.6, 0.88, n1) * smoothstep(0.48, 0.8, n2);
      const cool = smoothstep(0.42, 0.78, n2) * (1 - cloud * 0.45);
      // Fade toward the edges so the cloud floats in the sky.
      const ex = 1 - Math.pow(Math.abs(x / NEBULA_W - 0.5) * 2, 2.4);
      const ey = 1 - Math.pow(Math.abs(y / NEBULA_H - 0.45) * 2, 2.2);
      const edge = Math.max(0, ex * ey);
      const dark = smoothstep(0.55, 0.85, dust) * 0.55;
      let r = ar * cloud + cr * cool + wr * ridge * 0.55;
      let g = ag * cloud + cg * cool + wg * ridge * 0.55;
      let b = ab * cloud + cb * cool + wb * ridge * 0.45;
      const sum = cloud + cool + ridge * 0.55;
      if (sum > 0) { r /= sum; g /= sum; b /= sum; }
      const gas = shapeCloudDensity(cloud * 0.8 + cool * 0.4 + ridge * 0.55, densityAt(x / NEBULA_W, y / NEBULA_H), formation);
      const alpha = (1 - Math.exp(-gas)) * edge * (1 - dark);
      const i = (y * NEBULA_W + x) * 4;
      data[i] = r; data[i + 1] = g; data[i + 2] = b; data[i + 3] = Math.round(alpha * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

export default class NightSky extends Component {
  canvasRef = null;
  rootRef = null;
  layers = makeLayers();
  pointer = { tx: 0, ty: 0, x: 0, y: 0 };
  meteor = null;
  nextMeteorAt = 4;

  nebulaRefs = [null, null];
  glRef = null;
  // Eased nebula state. `cur` chases `target`; the renderer only ever
  // sees `cur`, so every class change is a continuous flow.
  neb = { cur: null, target: null, last: null };
  // Eased heading of the star field; `swing` per layer turns it into parallax.
  view = { heading: 0, target: 0 };
  figureWeights = FIGURE_KEYS.map(() => 0);
  journey = { camera: 0, target: -1, reveal: 0 };
  drawnSigns = FIGURE_KEYS.map(() => 0);

  componentDidMount() {
    this.reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.sprites = makeStarSprites();
    advanceJourney(this.journey, FIGURE_KEYS.indexOf(this.props.figureKey), 0, this.reduced);
    this.view.target = headingFor(this.props.accent);
    this.view.heading = this.view.target;
    this.gl = this.glRef ? createNebulaRenderer(this.glRef) : null;
    if (this.gl) {
      this.gl.resize(NEBULA_GL_W, NEBULA_GL_H);
      this.setNebulaTarget(this.props.accent, true);
      if (this.reduced) this.renderNebula(0);
    } else {
      this.paintNebulae();
    }
    this.resize();
    this.loadFigures();
    this.ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.resize()) : null;
    if (this.ro && this.rootRef) this.ro.observe(this.rootRef);
    document.addEventListener('visibilitychange', this.handleVisibility);
    if (!this.reduced) window.addEventListener('mousemove', this.handleMouse, { passive: true });
    this.start();
  }

  loadFigures() {
    const generation = this.figureGeneration = (this.figureGeneration || 0) + 1;
    loadFigureFields(this.props.humoursSign).then((loaded) => {
      if (this.disposed || !loaded || generation !== this.figureGeneration) return;
      this.figureFields = loaded.fields;
      this.gl?.setFigures(loaded.atlas);
      if (!this.gl) this.paintNebulae();
      if (this.reduced) {
        easeFigureWeights(this.figureWeights, FIGURE_KEYS.indexOf(this.props.figureKey), 0, true);
        this.renderNebula(0);
        this.draw(0);
      }
    });
  }

  componentDidUpdate(prev) {
    if (prev.humoursSign !== this.props.humoursSign) this.loadFigures();
    if (prev.figureKey !== this.props.figureKey && !this.gl) this.paintNebulae();
    if (prev.figureKey !== this.props.figureKey && this.reduced) {
      advanceJourney(this.journey, FIGURE_KEYS.indexOf(this.props.figureKey), 0, true);
      easeFigureWeights(this.figureWeights, FIGURE_KEYS.indexOf(this.props.figureKey), 0, true);
      this.renderNebula(0);
      this.draw(0);
    }
    if (prev.accent === this.props.accent) return;
    this.view.target = headingFor(this.props.accent);
    if (this.reduced) { this.view.heading = this.view.target; this.draw(0); }
    if (this.gl) {
      this.setNebulaTarget(this.props.accent, this.reduced);
      if (this.reduced) this.renderNebula(0);
    } else {
      this.paintNebulae();
    }
  }

  setNebulaTarget(accent, snap) {
    const key = accent || 'sky';
    const pal = paletteFor(accent || '#8fa8c8');
    const target = { shift: shiftFor(key), dust: pal.dust, mid: pal.mid, accent: pal.accent, ridge: pal.ridge };
    this.neb.target = target;
    if (snap || !this.neb.cur) {
      this.neb.cur = { shift: [...target.shift], dust: [...target.dust], mid: [...target.mid], accent: [...target.accent], ridge: [...target.ridge] };
    }
  }

  // Exponential easing toward the target: shape a little slower than
  // colour, so the clouds are still settling as the new tint arrives.
  easeNebula(dt) {
    const { cur, target } = this.neb;
    if (!cur || !target) return;
    const kShape = 1 - Math.exp(-dt / 1.9);
    const kColor = 1 - Math.exp(-dt / 1.2);
    for (let i = 0; i < 2; i++) cur.shift[i] += (target.shift[i] - cur.shift[i]) * kShape;
    for (const name of ['dust', 'mid', 'accent', 'ridge']) {
      for (let i = 0; i < 3; i++) cur[name][i] += (target[name][i] - cur[name][i]) * kColor;
    }
  }

  renderNebula(t) {
    const { cur } = this.neb;
    if (!this.gl || !cur || !this.w || !this.h) return;
    const rect = this.getFigureRect();
    this.gl.render({
      time: t * 0.32,
      shift: [cur.shift[0] + this.journey.camera * 0.9, cur.shift[1]],
      par: [this.pointer.x, this.pointer.y],
      dust: cur.dust,
      mid: cur.mid,
      accent: cur.accent,
      ridge: cur.ridge,
      intensity: 1.18,
      figureWeights: this.figureWeights.map((weight, i) => this.figureFields?.[i] ? weight : 0),
      figureRect: [rect.x / this.w, rect.y / this.h, rect.width / this.w, rect.height / this.h],
      figureStrength: this.props.subdued ? 0.5 : 0.95,
    });
  }

  paintNebulae() {
    const accent = this.props.accent || '#8fa8c8';
    const width = this.w || 1600; const height = this.h || 900;
    const rect = this.w && this.h ? this.getFigureRect() : figureRect(width, height);
    const selected = FIGURE_KEYS.indexOf(this.props.figureKey);
    const weights = FIGURE_KEYS.map((_, i) => i === selected ? 1 : 0);
    const densityAt = (x, y) => sampleFigureDensity(this.figureFields, weights,
      (x * width - rect.x) / rect.width, (y * height - rect.y) / rect.height);
    const formation = this.figureFields?.[selected] ? (this.props.subdued ? 0.5 : 0.95) : 0;
    this.nebulaRefs.forEach((el, i) => {
      if (!el) return;
      const src = makeNebula(accent, 11 + i * 29, densityAt, formation);
      if (!src) return;
      el.width = NEBULA_W; el.height = NEBULA_H;
      el.getContext('2d')?.drawImage(src, 0, 0);
    });
  }

  componentWillUnmount() {
    this.disposed = true;
    this.stop();
    this.gl?.dispose();
    this.ro?.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('mousemove', this.handleMouse);
  }

  handleVisibility = () => {
    if (document.hidden) this.stop(); else this.start();
  };

  handleMouse = (e) => {
    const rect = this.rootRef?.getBoundingClientRect();
    if (!rect || !rect.width) return;
    this.pointer.tx = Math.max(-1, Math.min(1, ((e.clientX - rect.left) / rect.width - 0.5) * 2));
    this.pointer.ty = Math.max(-1, Math.min(1, ((e.clientY - rect.top) / rect.height - 0.5) * 2));
  };

  start() {
    if (this.raf || !this.canvasRef) return;
    if (this.reduced) { this.draw(0); return; }
    const tick = (t) => { this.draw(t / 1000); this.raf = requestAnimationFrame(tick); };
    this.raf = requestAnimationFrame(tick);
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  resize() {
    const c = this.canvasRef;
    const rect = this.rootRef?.getBoundingClientRect();
    if (!c || !rect || rect.width === 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = rect.width; this.h = rect.height; this.dpr = dpr;
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    if (this.reduced) { this.renderNebula(0); this.draw(0); }
  }

  // Every sign is drawn at the same height. The atlas fits each engraving
  // into a portrait cell, so a wide seated pose would otherwise come out
  // half the size of a standing one.
  signGrowth(index) {
    const sign = CLASS_SIGNS[FIGURE_KEYS[index]];
    if (!sign) return 1;
    const fit = Math.min((FIELD_W - 24) / sign.width, (FIELD_H - 24) / sign.height);
    return SIGN_HEIGHT / (sign.height * fit / FIELD_H);
  }

  getFigureRect(index = this.journey.target) {
    const rect = figureRect(this.w, this.h);
    const humours = this.props.humoursSign && FIGURE_KEYS[index] === 'alchemist';
    if (!humours) {
      const growth = this.signGrowth(index);
      rect.x -= rect.width * (growth - 1) / 2;
      rect.y -= rect.height * (growth - 1) / 2;
      rect.width *= growth;
      rect.height *= growth;
    }
    if (humours) {
      // The tree box has a stable responsive layout. Measure its foundation
      // anchor so previews and differing header heights align identically.
      const anchor = this.rootRef?.parentElement?.querySelector('[data-humours-tree]');
      if (anchor) {
        const bounds = anchor.getBoundingClientRect();
        const sky = this.rootRef.getBoundingClientRect();
        const fit = Math.min((FIELD_W - 24) / HUMOURS_SIGN.width, (FIELD_H - 24) / HUMOURS_SIGN.height);
        const imageW = HUMOURS_SIGN.width * fit / FIELD_W * rect.width;
        const imageH = HUMOURS_SIGN.height * fit / FIELD_H * rect.height;
        rect.x = (bounds.left - sky.left + bounds.width * 0.5) / (sky.width / this.w)
          - (rect.width - imageW) / 2 - imageW * HUMOURS_SIGN.cross[0];
        rect.y = (bounds.top - sky.top + bounds.height * 320 / 600) / (sky.height / this.h)
          - (rect.height - imageH) / 2 - imageH * HUMOURS_SIGN.cross[1];
      }
    }
    rect.x += ((Math.max(0, index) * SIGN_SPACING) - this.journey.camera) * this.w;
    rect.x += this.pointer.x * 8 * this.w / 1600;
    rect.y += this.pointer.y * 5 * this.w / 1600;
    return rect;
  }

  // Ease the lean toward the pointer and publish it for the CSS layers.
  updatePointer() {
    const p = this.pointer;
    p.x += (p.tx - p.x) * 0.055;
    p.y += (p.ty - p.y) * 0.055;
    const host = this.rootRef?.parentElement;
    if (host) {
      host.style.setProperty('--vh-par-x', p.x.toFixed(3));
      host.style.setProperty('--vh-par-y', p.y.toFixed(3));
    }
  }

  updateMeteor(t, scale) {
    if (!this.meteor && t >= this.nextMeteorAt) {
      const rnd = Math.random;
      const fromLeft = rnd() < 0.5;
      this.meteor = {
        x: (fromLeft ? 0.05 + rnd() * 0.3 : 0.65 + rnd() * 0.3) * this.w,
        y: (0.05 + rnd() * 0.35) * this.h,
        vx: (fromLeft ? 1 : -1) * (520 + rnd() * 260) * scale,
        vy: (160 + rnd() * 120) * scale,
        born: t,
        life: 0.9 + rnd() * 0.5,
        len: (120 + rnd() * 90) * scale,
      };
    }
    if (this.meteor && t - this.meteor.born > this.meteor.life) {
      this.meteor = null;
      this.nextMeteorAt = t + 7 + Math.random() * 9;
    }
  }

  drawMeteor(ctx, t) {
    const m = this.meteor;
    if (!m) return;
    const age = t - m.born;
    const k = age / m.life;
    const fade = k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85;
    const hx = m.x + m.vx * age;
    const hy = m.y + m.vy * age;
    const mag = Math.hypot(m.vx, m.vy);
    const tx = hx - (m.vx / mag) * m.len;
    const ty = hy - (m.vy / mag) * m.len;
    const g = ctx.createLinearGradient(hx, hy, tx, ty);
    g.addColorStop(0, `rgba(255,255,255,${0.95 * fade})`);
    g.addColorStop(0.25, `rgba(230,238,255,${0.5 * fade})`);
    g.addColorStop(1, 'rgba(230,238,255,0)');
    ctx.strokeStyle = g;
    ctx.lineWidth = 1.6 * (this.w / 1600);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(hx, hy);
    ctx.lineTo(tx, ty);
    ctx.stroke();
  }

  draw(t) {
    const c = this.canvasRef;
    if (!c || !this.w) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const { w, h, dpr } = this;
    const scale = w / 1600;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (!this.reduced) {
      this.updatePointer();
      this.updateMeteor(t, scale);
      const last = this.neb.last;
      this.neb.last = t;
      const dt = last == null ? 0 : Math.min(0.1, Math.max(0, t - last));
      advanceJourney(this.journey, FIGURE_KEYS.indexOf(this.props.figureKey), dt);
      this.easeNebula(dt);
      easeFigureWeights(this.figureWeights, FIGURE_KEYS.indexOf(this.props.figureKey), dt);
      this.view.heading += (this.view.target - this.view.heading) * (1 - Math.exp(-dt / 2.4));
      this.renderNebula(t);
    }
    const px = this.pointer.x;
    const py = this.pointer.y;
    const pad = 24 * scale;
    const poleX = POLE.x * w;
    const poleY = POLE.y * w;
    const rect = this.getFigureRect();
    const formation = this.figureFields ? this.figureWeights.reduce((sum, weight) => sum + weight, 0) : 0;
    const densityAt = (x, y) => sampleFigureDensity(this.figureFields, this.figureWeights,
      (x - rect.x) / rect.width, (y - rect.y) / rect.height);
    // Stars add light; overlapping glares sum instead of stacking flat discs.
    ctx.globalCompositeOperation = 'lighter';
    for (const layer of this.layers) {
      const turn = (this.reduced ? 0 : t * layer.rot) + this.view.heading * layer.swing;
      const lx = -px * layer.parallax * scale - this.journey.camera * w * (0.12 + layer.parallax / 100);
      const ly = -py * layer.parallax * scale * 0.6;
      for (const s of layer.stars) {
        const ang = s.ang + turn;
        const wob = this.reduced ? 0 : Math.sin(t * 0.07 * s.speed + s.phase) * s.wander * scale;
        let x = poleX + Math.cos(ang) * s.dist * w + lx + wob;
        let y = poleY + Math.sin(ang) * s.dist * w + ly;
        if (x < -pad || x > w + pad || y < -pad || y > h + pad) continue;
        // Keep every star's identity, orbit and twinkle. Existing stars
        // respond to the cloud field; no extra figure particle layer exists.
        const density = densityAt(x, y);
        const pull = (this.props.subdued ? 8 : 22) * scale;
        x += (densityAt(x + 12 * scale, y) - densityAt(x - 12 * scale, y)) * pull;
        y += (densityAt(x, y + 12 * scale) - densityAt(x, y - 12 * scale)) * pull;
        const a = Math.min(1, s.base * (1 - s.flick + s.flick * Math.sin(t * s.speed + s.phase)) * (1 - formation * 0.15 + density * 1.5));
        const r = s.r * scale;
        const sprite = this.sprites?.[s.tint];
        if (!sprite || !layer.glow) {
          // Far and middle stars stay points of one or two pixels; brightness
          // stands in for size. Sprites here would cost more fill than the
          // whole nebula for glare nobody can see at that scale.
          ctx.globalAlpha = a * Math.min(1, 0.5 + r * 0.6);
          ctx.fillStyle = STAR_CSS[s.tint];
          const side = Math.max(1, Math.min(2, r));
          ctx.fillRect(x - side / 2, y - side / 2, side, side);
          continue;
        }
        // Near stars carry the point spread function: tight core, inverse
        // square glare, spikes on the brightest. The sprite scales with the
        // star so the core stays sharp instead of blurring into a blob. The
        // sizes are kept modest: blended sprite fill is the sky's real cost.
        const img = s.spike ? sprite.spike : sprite.glare;
        const size = (s.spike ? 44 + r * 14 : 16 + r * 8) * scale;
        ctx.globalAlpha = a;
        ctx.drawImage(img, x - size / 2, y - size / 2, size, size);
      }
    }
    this.drawFigureConstellation(ctx, scale, t);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    this.drawMeteor(ctx, t);
  }

  drawFigureConstellation(ctx, scale, t) {
    ctx.save();
    ctx.strokeStyle = '#cddfeb';
    ctx.fillStyle = '#f1f5ff';
    ctx.lineWidth = Math.max(0.5, scale * 0.7);
    for (let i = 0; i < FIGURE_KEYS.length; i++) {
      const weight = (this.props.subdued ? 0.3 : 1) * this.figureWeights[i];
      const signRect = this.getFigureRect(i);
      if (signRect.x > this.w || signRect.x + signRect.width < 0) continue;
      const reveal = i === this.journey.target ? this.journey.reveal : this.drawnSigns[i];
      this.drawnSigns[i] = reveal;
      const field = this.figureFields?.[i];
      if (!field?.image || weight < 0.005) continue;
      const sign = field.sign || CLASS_SIGNS[FIGURE_KEYS[i]];
      // Match the contained drawing used to prepare the cloud guide.
      const fit = Math.min((FIELD_W - 24) / sign.width, (FIELD_H - 24) / sign.height);
      const width = sign.width * fit / FIELD_W * signRect.width;
      const height = sign.height * fit / FIELD_H * signRect.height;
      const left = signRect.x + (signRect.width - width) / 2;
      const top = signRect.y + (signRect.height - height) / 2;
      const stars = sign.stars.map(([x, y, brightness]) => [left + x * width, top + y * height, brightness]);
      // The trace spreads from stellar landmarks after the connecting
      // segments begin growing. Unioned clips reveal ink, not opacity.
      const ink = Math.max(0, (reveal - 0.25) / 0.75);
      if (ink > 0) {
        ctx.save();
        ctx.beginPath();
        stars.forEach(([x, y], index) => {
          const growth = Math.max(0, (ink - index / stars.length * 0.12) / 0.88);
          const radius = growth * Math.max(width, height) * 0.65;
          ctx.moveTo(x + radius, y);
          ctx.arc(x, y, radius, 0, Math.PI * 2);
        });
        ctx.clip();
        ctx.globalAlpha = weight * 0.28;
        ctx.drawImage(field.image, left, top, width, height);
        ctx.restore();
      }
      ctx.globalAlpha = weight * 0.23;
      ctx.beginPath();
      for (const [index, [from, to]] of sign.links.entries()) {
        const progress = linkProgress(reveal, index, sign.links.length);
        ctx.moveTo(stars[from][0], stars[from][1]);
        ctx.lineTo(stars[from][0] + (stars[to][0] - stars[from][0]) * progress,
          stars[from][1] + (stars[to][1] - stars[from][1]) * progress);
      }
      ctx.stroke();
      const sprite = this.sprites?.[1];
      stars.forEach(([x, y, brightness], index) => {
        const twinkle = this.reduced ? 1 : 0.88 + 0.12 * Math.sin(t * 0.8 + index * 2.4);
        ctx.globalAlpha = weight * brightness * twinkle;
        if (sprite) {
          const img = brightness >= 0.9 ? sprite.spike : sprite.glare;
          const size = (brightness >= 0.9 ? 64 + brightness * 40 : 26 + brightness * 22) * scale;
          ctx.drawImage(img, x - size / 2, y - size / 2, size, size);
        } else {
          ctx.beginPath();
          ctx.arc(x, y, (1.1 + brightness * 0.85) * scale, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    }
    ctx.restore();
  }

  render() {
    const { accent } = this.props;
    return (
      <div
        ref={(el) => { this.rootRef = el; }}
        aria-hidden="true"
        data-figure={this.props.figureKey || ''}
        data-figure-variant={this.props.humoursSign ? 'four-humours' : 'class'}
        className="vh-sky absolute inset-0 pointer-events-none overflow-hidden"
        style={{ '--vh-sky-accent': accent || '#8fa8c8' }}
      >
        <canvas ref={(el) => { this.glRef = el; }} className="vh-sky-nebula-gl" />
        <div className="vh-par-layer" style={{ '--vh-par-depth': 0 }}>
          <canvas ref={(el) => { this.nebulaRefs[0] = el; }} className="vh-sky-nebula-fallback" />
        </div>
        <div className="vh-par-layer" style={{ '--vh-par-depth': 0 }}>
          <canvas ref={(el) => { this.nebulaRefs[1] = el; }} className="vh-sky-nebula-fallback" />
        </div>
        <div className="vh-par-layer" style={{ '--vh-par-depth': 14 }}>
          <div className="vh-sky-aurora" />
        </div>
        <canvas ref={(el) => { this.canvasRef = el; }} className="absolute inset-0 w-full h-full" />
      </div>
    );
  }
}
